import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { orderConflictRetryPolicy } from '../../../service/api/conflictRetry';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders/{tokenValue}/items/{orderItemId}';
const HTTP_METHOD = 'patch';

export type MakeEditProductVariantQuantityInOrderDeps<OShowType> = {
  readonly orderShow: Schema.Codec<OShowType, unknown>;
};

export const makeEditProductVariantQuantityInOrder = <OShowType>(
  deps: MakeEditProductVariantQuantityInOrderDeps<OShowType>
) => {
  const EditProductVariantQuantityError = createBackendApiError({
    domain: 'Order',
    endpoint: 'EditProductVariantQuantity',
    path: ENDPOINT,
    identifierField: 'orderToken',
  } as const);

  const patchOrderItemFetch = (tokenValue: string, orderItemId: number, quantity: number) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json', 'application/merge-patch+json')
        .fetch({
          params: { tokenValue, orderItemId: String(orderItemId) },
          body: {
            quantity,
          },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'order.token': tokenValue,
          'orderItem.id': String(orderItemId),
          'orderItem.quantity': String(quantity),
        },
      })
    );

  const editProductVariantQuantityInOrder = (
    tokenValue: string,
    orderItemId: number,
    quantity: number
  ) =>
    patchOrderItemFetch(tokenValue, orderItemId, quantity).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.orderShow)(response),
          404: (response) =>
            Effect.fail(
              new EditProductVariantQuantityError.NotFoundError({
                orderToken: tokenValue,
                response,
              })
            ),
          422: EditProductVariantQuantityError.createViolationError(tokenValue),
          orElse: (response) =>
            Effect.fail(
              new EditProductVariantQuantityError.ApiRequestError({
                orderToken: tokenValue,
                response,
              })
            ),
        })
      ),
      Effect.retry(orderConflictRetryPolicy),
      Effect.tap(() =>
        logApiOperation(
          ApiModule.OrderItemEdit,
          'Debug',
          `Successfully updated order item ${String(orderItemId)} quantity to ${String(quantity)} in order ${tokenValue}`
        )
      ),
      Effect.withSpan('api.orderItem.edit', {
        kind: 'internal',
        attributes: {
          'order.token': tokenValue,
          'orderItem.id': String(orderItemId),
          'orderItem.quantity': String(quantity),
        },
      })
    );

  return { editProductVariantQuantityInOrder, EditProductVariantQuantityError };
};
